import React, { useEffect, useState, useRef } from "react";
import { Table, Thead, Tbody, Tr, Th, Td, Spinner, useTheme, Box, Input, IconButton, InputGroup, InputRightElement, Tooltip } from "@chakra-ui/react";
import { SearchIcon, CloseIcon } from "@chakra-ui/icons";
import axios from "axios";

const Table1 = ({ supplier, retailer }) => {
  const [thead, setThead] = useState([]);
  const [tbody, setTbody] = useState([]);
  const [filteredTbody, setFilteredTbody] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [showTooltip, setShowTooltip] = useState(false);
  const theme = useTheme();
  const syncingRef = useRef(false);

  // Sticky scrollbar sync
  useEffect(() => {
    const tableContainer = document.getElementById("margin-table-container");
    const stickyScrollbar = document.getElementById("margin-sticky-scrollbar");
    const scrollbarContent = document.getElementById("margin-scrollbar-content");

    if (!tableContainer || !stickyScrollbar || !scrollbarContent) return;

    const updateScrollbarWidth = () => {
      const table = tableContainer.querySelector("table");
      if (table) {
        const tableWidth = table.scrollWidth;
        const containerWidth = tableContainer.clientWidth;
        scrollbarContent.style.width = tableWidth + "px";
        stickyScrollbar.style.display = tableWidth > containerWidth ? "block" : "none";
      }
    };

    const syncTableScroll = () => {
      if (syncingRef.current) return;
      syncingRef.current = true;
      requestAnimationFrame(() => {
        stickyScrollbar.scrollLeft = tableContainer.scrollLeft;
        syncingRef.current = false;
      });
    };

    const syncStickyScroll = () => {
      if (syncingRef.current) return;
      syncingRef.current = true;
      requestAnimationFrame(() => {
        tableContainer.scrollLeft = stickyScrollbar.scrollLeft;
        syncingRef.current = false;
      });
    };

    setTimeout(() => {
      updateScrollbarWidth();
      stickyScrollbar.scrollLeft = tableContainer.scrollLeft;
    }, 150);

    tableContainer.addEventListener("scroll", syncTableScroll, { passive: true });
    stickyScrollbar.addEventListener("scroll", syncStickyScroll, { passive: true });

    const handleResize = () => setTimeout(updateScrollbarWidth, 100);
    window.addEventListener("resize", handleResize);

    const observer = new MutationObserver(() => setTimeout(updateScrollbarWidth, 50));
    observer.observe(tableContainer, { childList: true, subtree: true, attributes: true });

    return () => {
      tableContainer.removeEventListener("scroll", syncTableScroll);
      stickyScrollbar.removeEventListener("scroll", syncStickyScroll);
      window.removeEventListener("resize", handleResize);
      observer.disconnect();
    };
  }, [tbody, thead]);

  // Drag-to-scroll
  useEffect(() => {
    const el = document.getElementById("margin-table-container");
    if (!el) return;

    let down = false;
    let startX = 0;
    let scrollStart = 0;

    const downHandler = (e) => {
      if (e.button !== 0) return;
      down = true;
      startX = e.clientX;
      scrollStart = el.scrollLeft;
      el.style.cursor = "grabbing";
      el.style.userSelect = "none";
    };

    const moveHandler = (e) => {
      if (!down) return;
      el.scrollLeft = scrollStart - (e.clientX - startX);
    };

    const upHandler = () => {
      if (!down) return;
      down = false;
      el.style.cursor = "grab";
      el.style.removeProperty("user-select");
    };

    el.addEventListener("mousedown", downHandler);
    window.addEventListener("mousemove", moveHandler);
    window.addEventListener("mouseup", upHandler);

    return () => {
      el.removeEventListener("mousedown", downHandler);
      window.removeEventListener("mousemove", moveHandler);
      window.removeEventListener("mouseup", upHandler);
    };
  }, []);

  // Fetch data
  useEffect(() => {
    setLoading(true);
    const buildTable = async () => {
      try {
        const res = await axios.post(`${process.env.REACT_APP_API_URL}/rep_margin/table1`, {
          supplier,
          retailer,
          userid: localStorage.getItem("userid"),
        });
        setThead(res.data.data.tableData.thead);
        setTbody(res.data.data.tableData.tbody);
        setFilteredTbody(res.data.data.tableData.tbody);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching table data:", error);
        setLoading(false);
      }
    };
    buildTable();
  }, [supplier, retailer]);

  // Search
  const handleSearch = () => {
    const lowercasedFilter = searchText.toLowerCase();
    const filteredData = tbody.filter((item) => {
      return item.some((col, index) => {
        if (index === 1 || index === 2 || index === 3) {
          const colData = Array.isArray(col) ? col[0] : col;
          return colData.toString().toLowerCase().includes(lowercasedFilter);
        }
        return false;
      });
    });
    setFilteredTbody(filteredData);
  };

  const handleClearSearch = () => {
    setSearchText("");
    setFilteredTbody(tbody);
  };

  useEffect(() => {
    if (searchText) {
      setShowTooltip(true);
      const timer = setTimeout(() => setShowTooltip(false), 5000);
      return () => clearTimeout(timer);
    } else {
      setShowTooltip(false);
    }
  }, [searchText]);

  // Column widths and sticky config
  const getColWidth = (index) => (index === 0 ? "80px" : index === 1 ? "180px" : index === 2 ? "80px" : "70px");
  const isSticky = (index) => index < 2;
  const getStickyLeft = (index) => (index === 0 ? 0 : index === 1 ? "80px" : "auto");

  // Body cell styling from class names
  const getBodyCellStyle = (className) => {
    switch (className) {
      case "td_RSPTotal":
        return { fontWeight: "bold" };
      case "td_marginTotal":
        return { fontWeight: "bold" };
      case "td_RSPGrandTotal":
        return { fontWeight: "bold", bg: "white !important" };
      case "td_marginGrandTotal":
        return { fontWeight: "bold", bgGradient: "linear(to-r, #fff, rgba(255, 107, 15, 0.45))" };
      default:
        return {};
    }
  };

  // Extract class name from cell attributes
  const getCellClassName = (col) => {
    if (!Array.isArray(col) || !col[1]) return null;
    const attrs = col[1];
    if (Array.isArray(attrs)) {
      const classAttr = attrs.find((a) => a.key === "class");
      return classAttr ? classAttr.value : null;
    }
    return attrs.key === "class" ? attrs.value : null;
  };

  // Extract colspan from cell attributes
  const getCellColspan = (col) => {
    if (!Array.isArray(col) || !col[1]) return undefined;
    const attrs = col[1];
    if (Array.isArray(attrs)) {
      const colspanAttr = attrs.find((a) => a.key === "colspan");
      return colspanAttr ? colspanAttr.value : undefined;
    }
    return attrs.key === "colspan" ? attrs.value : undefined;
  };

  if (loading) return <Spinner />;

  return (
    <Box>
      {/* Search Bar */}
      <Box p={3} borderBottom="1px solid" borderColor="gray.100">
        <InputGroup size="sm" width="300px">
          <Tooltip label="Press Enter to search, Esc to clear" isOpen={showTooltip} placement="right">
            <Input
              pr="4.5rem"
              type="text"
              placeholder="Search articles..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearch();
                else if (e.key === "Escape") handleClearSearch();
              }}
              borderColor="gray.300"
              _focus={{ borderColor: theme.colors.primary1 }}
            />
          </Tooltip>
          <InputRightElement width="4.5rem">
            {searchText && <IconButton h="1.5rem" size="xs" onClick={handleClearSearch} icon={<CloseIcon />} mr={1} />}
            <IconButton h="1.5rem" size="xs" onClick={handleSearch} icon={<SearchIcon />} />
          </InputRightElement>
        </InputGroup>
      </Box>

      <Box position="relative">
        {/* Sticky Horizontal Scrollbar */}
        <Box
          id="margin-sticky-scrollbar"
          position="sticky"
          top={0}
          left={0}
          right={0}
          height="18px"
          bg="white"
          borderBottom="2px solid"
          borderColor={theme.colors.primary1}
          zIndex={25}
          display="none"
          overflowX="auto"
          overflowY="hidden"
          css={{
            scrollBehavior: "auto",
            "&::-webkit-scrollbar": { height: "14px" },
            "&::-webkit-scrollbar-track": { background: "#f1f5f9", borderRadius: "4px" },
            "&::-webkit-scrollbar-thumb": { background: theme.colors.primary1, borderRadius: "4px", border: "1px solid #e2e8f0" },
            "&::-webkit-scrollbar-thumb:hover": { background: theme.colors.primary2 },
          }}
        >
          <Box id="margin-scrollbar-content" height="1px" minWidth="100%" />
        </Box>

        <Box id="margin-table-container" overflowX="auto" overflowY="hidden" sx={{ cursor: "grab", scrollBehavior: "auto" }}>
          <Table
            size="sm"
            variant="unstyled"
            width="max-content"
            sx={{
              minWidth: "max-content",
              tableLayout: "fixed",
              "th:nth-of-type(n+4), td:nth-of-type(n+4)": {
                maxWidth: "70px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
            }}
          >
            <Thead>
              {thead.map((row, rowIndex) => (
                <Tr key={`thead-row-${rowIndex}`}>
                  {row.map((col, colIndex) => {
                    const colData = Array.isArray(col) ? col[0] : col;
                    const colSpan = getCellColspan(col);
                    const headerBg = rowIndex === 0 ? theme.colors.primary2 : theme.colors.secondary1;

                    return (
                      <Th
                        key={`thead-${rowIndex}-${colIndex}`}
                        colSpan={colSpan}
                        width={getColWidth(colIndex)}
                        bg={headerBg}
                        color="white"
                        position={isSticky(colIndex) ? "sticky" : "static"}
                        left={getStickyLeft(colIndex)}
                        zIndex={isSticky(colIndex) ? 22 : 20}
                        px={2}
                        py={rowIndex === 0 ? 2 : 1.5}
                        fontSize={rowIndex === 0 ? "10px" : "9px"}
                        fontWeight={rowIndex === 0 ? "bold" : "medium"}
                        textTransform="uppercase"
                        letterSpacing="wide"
                        borderRight="1px solid"
                        borderColor="whiteAlpha.400"
                        shadow={isSticky(colIndex) ? "md" : "none"}
                        lineHeight={rowIndex === 0 ? "1.2" : "1.1"}
                        textAlign={colSpan > 1 ? "center" : "left"}
                      >
                        <Box overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                          {colData}
                        </Box>
                      </Th>
                    );
                  })}
                </Tr>
              ))}
            </Thead>
            <Tbody>
              {filteredTbody.map((row, rowIndex) => {
                const rowBg = rowIndex % 2 === 0 ? "gray.50" : "white";
                return (
                  <Tr key={`row-${rowIndex}`} bg={rowBg} _hover={{ bg: "blue.50" }} transition="all 0.15s" borderBottom="1px solid" borderColor="gray.200">
                    {row.map((col, colIndex) => {
                      const colData = Array.isArray(col) ? col[0] : col;
                      const className = getCellClassName(col);
                      const cellStyle = className ? getBodyCellStyle(className) : {};

                      return (
                        <Td
                          key={`cell-${rowIndex}-${colIndex}`}
                          sx={{
                            backgroundColor: isSticky(colIndex) ? rowBg : undefined,
                            ...cellStyle,
                          }}
                          width={getColWidth(colIndex)}
                          minW={getColWidth(colIndex)}
                          borderRight="1px solid"
                          borderColor="gray.200"
                          position={isSticky(colIndex) ? "sticky" : "static"}
                          left={getStickyLeft(colIndex)}
                          zIndex={isSticky(colIndex) ? 15 : 1}
                          px={2}
                          py={1}
                          fontSize="11px"
                          fontWeight={isSticky(colIndex) ? "medium" : "normal"}
                          shadow={isSticky(colIndex) ? "sm" : "none"}
                          lineHeight="1.3"
                          whiteSpace="nowrap"
                        >
                          <Box overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" title={String(colData)} maxW="100%">
                            {colData}
                          </Box>
                        </Td>
                      );
                    })}
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      </Box>
    </Box>
  );
};

export default Table1;
